import {
  MEMBER_PREFERENCE_NAMES,
  MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING,
  MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL,
} from './constants'

export type MemberPreferencesForUserV2 = {
  ethnicities: string[]
  gender: string[]
  isBipoc: boolean
  isLgbtqia: boolean
  religions: string[]
  isExperiencedWithVeterans: boolean
  preferredSessionFormat: string[] // coaching preference
  preferredAppointmentType: string[] // therapy preference
  /** Comma-separated list of preference categories shown to the user (used for analytics / BE context). */
  visiblePreferences?: string
  availabilities?: string[] | null
  timeZone?: string
  customFreeTextPreference?: string
  parsePreferenceId?: string
}

/**
 * AI-parsed preferences from free-text input, displayed as removable word bubbles.
 * A single object can contain multiple preference keys.
 */
export type ParsedPreference = Partial<
  Omit<MemberPreferencesForUserV2, 'isBipoc' | 'timeZone' | 'customPreferencesText'>
>

export type ParsedPreferenceKey = keyof ParsedPreference

/**
 * Display order for parsed preferences (word bubbles).
 *
 * Order:
 * 1. Gender
 * 2. Day and time (availabilities)
 * 3. Race/ethnicity (ethnicities)
 * 4. LGBTQIA+ - boolean preference
 * 5. Religion
 * 6. Veterans - boolean preference
 * 7. Meeting setting (preferredAppointmentType - therapy, preferredSessionFormat - coaching)
 */
export const PARSED_PREFERENCE_DISPLAY_ORDER: ParsedPreferenceKey[] = [
  MEMBER_PREFERENCE_NAMES.GENDER,
  MEMBER_PREFERENCE_NAMES.AVAILABILITIES,
  MEMBER_PREFERENCE_NAMES.ETHNICITIES,
  MEMBER_PREFERENCE_NAMES.IS_LGBTQIA,
  MEMBER_PREFERENCE_NAMES.RELIGIONS,
  MEMBER_PREFERENCE_NAMES.IS_EXPERIENCED_WITH_VETERANS,
  MEMBER_PREFERENCE_NAMES.PREFERRED_APPOINTMENT_TYPE,
  MEMBER_PREFERENCE_NAMES.PREFERRED_SESSION_FORMAT,
]

/** Display item for a parsed preference bubble, including the value ID for individual removal */
export type ParsedPreferenceDisplayItem = {
  displayString: string
  /** The original value ID. Undefined for availabilities and boolean preferences which should remove entire preference */
  valueId?: string
  /** The preference key (e.g., 'gender', 'ethnicities', 'isLgbtqia') */
  preferenceKey: ParsedPreferenceKey
}

export type ProviderIdentity = {
  isBipoc: boolean
  isLgbtqia: boolean
}

export type ProviderSearchPreferencesV2 = {
  ethnicities: string
  gender: string
  isLgbtqia: boolean
  religions: string
  isExperiencedWithVeterans: boolean
  modalityPreference: string
  isBipoc: boolean
  visiblePreferences: string
  preferenceVariations: string | null
  customFreeTextPreference?: string | null
}

export type ProviderSearchPreferences = ProviderIdentity & {
  modalityPreference: string
}

// Based on Member preferences v1 schema
type UserPreferenceV1ValuesSimplified = {
  isBipoc?: boolean
  isLgbtqia?: boolean
  preferredSessionFormat?: string[]
  preferredAppointmentType?: string[]
}

// Based on Member preferences v2 schema
type UserPreferenceV2ValuesSimplified = {
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING.ethnicities]?: string[]
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING.gender]?: string[]
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING.isLgbtqia]?: boolean
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING.religions]?: string[]
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING.isExperiencedWithVeterans]?: boolean
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING.meetingSetting]?: string[]
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING.availabilities]?: string[] | null
}

// Same as UserPreferenceV2ValuesSimplified but can include conservative versions of gender and lgbtqia preferences
export type UserPreferenceV2ValuesSimplifiedForMixpanel = UserPreferenceV2ValuesSimplified & {
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.genderConservative]?: string[]
  [MEMBER_PREFERENCES_CATEGORY_SIMPLIFIED_MAPPING_FOR_MIXPANEL.isLgbtqiaConservative]?: boolean
}

// Represents a simplified object (with only one prop representing meeting preference) that is passed to mixpanel and used to count number of user preferences
export type UserPreferenceValuesSimplified = UserPreferenceV1ValuesSimplified | UserPreferenceV2ValuesSimplified

// Determines which page the user is coming from to view member preferences page
export enum PreferencesEntrypointMapping {
  RECOMMENDATIONS_EXPLAINED = 'recommendations explained',
  PROVIDER_RESULTS = 'provider results',
  CONNECTED_CALENDAR = 'connected calendar',
}
